import { Image, Spin } from 'antd';
import React, { useState } from 'react';
import ModalTitleLeft8 from '../../../common/modalTitle/ModalTitleLeft8';
import DocumentTypesGrid from './DocumentTypesGrid';

import {
    DocumentTypesModal,
    DocumentTypeTextBlock,
    IaLegend,
    DivFlexWithWrap,
    DivImpactArea,
    DivImpactAreaImage,
    ImpactAreaLogosContainer,
    ImpactAreaIconsContainer
} from './FindDocuments.styles';
import { useAppSelector } from '../../../../hooks/redux';
import { CloseIcon, PrimaryButton, SpaceEnd } from '../../../../styles/common.styles';
import { ImpactAreas } from '../../../../types/impactAreas/impactAreas.enum';
import { SustainabilityDocument } from '../../../../redux/actions/productActions/types';

interface Props {
    isOpen: boolean;
    closeModal: (data: { key: 'findDocuments' }) => void;
    documentTypesInfo: SustainabilityDocument[];
    reload: (modal: string) => void;
}

const FindDocuments: React.FC<Props> = ({ isOpen, closeModal, documentTypesInfo }) => {
    const selector = useAppSelector((combinedState) => combinedState);
    const { projectReducer } = selector;

    const [state, setState] = useState({
        curPage: 1,
        searchString: '',
        documentTypes: !documentTypesInfo ? Array<any>() : documentTypesInfo,
        currentDocumentTypes: !documentTypesInfo ? Array<any>() : documentTypesInfo.slice(0, 10),
        total: !documentTypesInfo ? 0 : documentTypesInfo.length,
        totalWithFilter: !documentTypesInfo ? 0 : documentTypesInfo.length,
    });

    const handleCancel = () => {
        closeModal({ key: 'findDocuments' });
    };

    const allImpactAreas = Object.values(ImpactAreas).filter((ia) => ia !== ImpactAreas.AllImpactAreas) as string[];

    const getInspectAreaImage = (impactAreaName : string) => {
        switch(impactAreaName) {
            case ImpactAreas.HumanHealth:
                return 'src/assets/images/health_red.svg';
            case ImpactAreas.ClimateHealth:
                return 'src/assets/images/climate_blue.svg';
            case ImpactAreas.Circularity:
                return 'src/assets/images/circularity_blue.svg';
            case ImpactAreas.SocialHealthAndEquity:
                return 'src/assets/images/social_purple.svg';
            case ImpactAreas.EcosystemHealth:
                return 'src/assets/images/ecosystem_green.svg';
            default:
                return '';
        }
    };

    const applyFilters = (page: number, searchString: string, impactArea: string) => {
        let filtered = [...state.documentTypes];

        // Filter by impact area
        if (impactArea !== ImpactAreas.AllImpactAreas) {
            filtered = filtered.filter((doc) =>
                doc.impactAreas.some((ia) => ia.title === impactArea)
            );
        }

        // Filter by search string
        if (searchString) {
            filtered = filtered.filter((doc) =>
                doc.title.toLowerCase().includes(searchString.toLowerCase())
            );
        }

        const totalCount = filtered.length;
        const start = page === 1 ? 0 : (page - 1) * 10;
        const paginated = filtered.slice(start, start + 10);

        setState((prev) => ({
            ...prev,
            currentDocumentTypes: paginated,
            totalWithFilter: totalCount,
            curPage: page,
            total: totalCount,
            searchString,
        }));
    };

    return (
        <>
        <DocumentTypesModal
            open={isOpen}
            onCancel={handleCancel}
            centered
            footer={null}
            closeIcon={<CloseIcon />}
        >
            <Spin spinning={projectReducer.pending}>
                <ModalTitleLeft8 title="Find CMF sustainability documents" icon="ic-modal-title-share.svg" />
                <DocumentTypeTextBlock>
                    <b>
                        Need more sustainability documents?&nbsp;
                    </b>
                    Visit any of the <a href="https://www.mindfulmaterials.com/a-common-language" target="_blank">mindful MATERIALS Common Materials Framework (CMF Prioritization v1.0)</a>
                    {' '}certification and reporting programs to download documents, or visit standards organization websites to learn more about them.
                </DocumentTypeTextBlock>

                <IaLegend>
                    <DivFlexWithWrap>
                        <ImpactAreaLogosContainer>
                            <DivImpactArea>
                                    <a href="https://www.mindfulmaterials.com/" target="_blank" rel="noopener noreferrer">
                                        <Image
                                            preview={false}
                                            src={'src/assets/images/mindful-materials-logo.svg'}
                                            style={{ cursor: 'pointer' }}
                                            width={200}
                                            height={26}
                                        />
                                    </a>
                                    <a href="https://www.mindfulmaterials.com/a-common-language" target="_blank" rel="noopener noreferrer">
                                        <Image
                                            preview={false}
                                            src={'src/assets/images/cmf-primary-logo.svg'}
                                            style={{ cursor: 'pointer' }}
                                            width={99}
                                            height={34}
                                        />
                                    </a>
                            </DivImpactArea>
                        </ImpactAreaLogosContainer>
                        <ImpactAreaIconsContainer>
                      {allImpactAreas?.map((impactArea, index) => (
                        <DivImpactArea key={index}>
                          <DivImpactAreaImage>
                             <Image
                               preview={false}
                               src={getInspectAreaImage(impactArea)}
                               style={{ cursor: 'none' }}
                               width={65} height={72}
                             />
                          </DivImpactAreaImage>
                        </DivImpactArea>
                      ))}
                      </ImpactAreaIconsContainer>
                    </DivFlexWithWrap>
                </IaLegend>
                <DocumentTypesGrid
                    totalCount={state.total}
                    totalWithFilterCount={state.totalWithFilter}
                    documentTypes={state.currentDocumentTypes}
                    getDocumentTypes={applyFilters}
                />

                <SpaceEnd style={{ marginTop: '12' + `px` ,  marginBottom: '16' + `px` }}>
                    <PrimaryButton
                        htmlType="submit"
                        width={127}
                        height={32}
                        onClick={handleCancel}
                    >
                        Done
                    </PrimaryButton>
                </SpaceEnd>

            </Spin>
        </DocumentTypesModal>
        </>
    );
};

export default FindDocuments;

